// Displacement post-adjust tracker — the ion-storm pattern, shared by the
// custom events that scale entity motion (event horizon, blood moon,
// chronal eddy).
//
// Why not scale `vel`: enemy-ai recomputes steering velocity and integrates
// position in the same pass, so scaling vel cannot produce a clean speed
// change (steering re-accelerates from the scaled value). Instead the event
// records each entity's position at the end of every event tick and, on the
// next tick, rescales the displacement that other systems integrated in
// between. Net motion is exactly `factor`, regardless of steering,
// separation pushes, or telegraph freezes (frozen entities have zero
// displacement — a no-op).
//
// Determinism (GDD Appendix A §A.1): pure function of world state — no RNG.
// Pool iteration stays in pool_index order; entity ids are unique per run,
// so released ids never alias.
//
// Perf: entry objects are reused across ticks (a fresh {x,y} is allocated
// only for newly seen ids, i.e. spawns), so steady-state update() allocates
// nothing. Two maps swap roles each tick instead of being rebuilt.

import type { Vec2 } from '../../types.js';
import { wrapArena, wrappedDelta } from '../collision.js';

/** Minimal shape a pool entity needs for displacement tracking. */
export interface TrackedEntity {
  id: number;
  pos: Vec2;
}

export class DisplacementScaler {
  private cur = new Map<number, { x: number; y: number }>();
  private next = new Map<number, { x: number; y: number }>();

  /** Drop all recorded positions (call from the event's start()/end()). */
  reset(): void {
    this.cur.clear();
    this.next.clear();
  }

  /**
   * Rescale this tick's displacement for every iterated entity. Entities
   * passing `gate` (evaluated on the current, post-integration position) end
   * the tick at prev + (pos − prev) · factor: 0.5 halves motion (slow), 1.35
   * amplifies it (frenzy). Entities without a recorded previous position
   * (just spawned, or first event tick) are only recorded, never rescaled.
   */
  apply<T extends TrackedEntity>(
    entities: Iterable<T>,
    factor: number,
    gate?: (e: T) => boolean
  ): void {
    const old = this.cur;
    const fresh = this.next;
    for (const e of entities) {
      let entry = old.get(e.id);
      if (entry !== undefined && (gate === undefined || gate(e))) {
        const d = wrappedDelta(e.pos, entry);
        e.pos.x = entry.x + d.x * factor;
        e.pos.y = entry.y + d.y * factor;
        wrapArena(e.pos);
      }
      if (entry === undefined) entry = { x: 0, y: 0 };
      entry.x = e.pos.x;
      entry.y = e.pos.y;
      fresh.set(e.id, entry);
    }
    old.clear();
    this.cur = fresh;
    this.next = old;
  }
}

/**
 * Single-entity variant for the player: two scalars instead of a map. The
 * gate receives BOTH the previous and the current position so callers can
 * require the whole displacement to have happened inside their zone — a
 * teleport (wormhole) into the zone must not get its jump rescaled.
 */
export class PlayerDisplacementScaler {
  private readonly prev: Vec2 = { x: 0, y: 0 };
  private has = false;

  reset(): void {
    this.has = false;
  }

  apply(player: TrackedEntity, factor: number, gate: (prev: Vec2, cur: Vec2) => boolean): void {
    if (this.has && gate(this.prev, player.pos)) {
      const d = wrappedDelta(player.pos, this.prev);
      player.pos.x = this.prev.x + d.x * factor;
      player.pos.y = this.prev.y + d.y * factor;
      wrapArena(player.pos);
    }
    this.prev.x = player.pos.x;
    this.prev.y = player.pos.y;
    this.has = true;
  }
}
